'use client'
import {
  email,
  literal,
  minLength,
  object,
  optional,
  Output,
  regex,
  string
} from 'valibot'
import React, { memo, useEffect, useState, useCallback } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { useSendApi } from '@/components/forms/hook'
import { Section } from '@/components/parts/section'
import { useRouter, usePathname } from 'next/navigation'
import { Button } from '@/components/parts/button'
import { ValidationError } from '@/components/forms/ui/validation-error'
import { FieldBlock } from '@/components/forms/ui/field-block'
import { Input } from '@/components/forms/ui/input'
import { Select } from '@/components/forms/ui/select'
import { Textarea } from '@/components/forms/ui/textarea'
import { Checkbox } from '@/components/parts/checkbox'
import { Heading } from '@/components/parts/heading'
import { Lead } from '@/components/parts/lead'
import { Department } from '@/types/newt/department'
import { useThanksForAppRouter } from '@/hooks/useThanksForAppRouter'
import { Opportunity } from '@/types/newt/opportunity'

const lawgueSolutionSchema = object({
  salesforceId: string(),
  company: string([minLength(1, '貴社名/貴所名をご入力ください')]),
  lastname: string([minLength(1, '必須項目です')]),
  firstname: string([minLength(1, '必須項目です')]),
  email: string([
    minLength(1, '必須項目です'),
    email('入力されたメールアドレスに間違いがあります')
  ]),
  phone_number: string([
    minLength(1, '必須項目です'),
    regex(/^\d{2,4}-\d{1,4}-\d{4}$/, '電話番号の形式を確認してください')
  ]),
  select_form1: string([minLength(1, '必須項目です')]),
  department: optional(string()),
  select_form2: string([minLength(1, '必須項目です')]),
  select_form3: string([minLength(1, '必須項目です')]),
  textarea: optional(string()),
  checkbox: literal(true, 'チェックを入れてください'),
  first_view_route: optional(string()),
  last_view_route: optional(string())
})

export type LawgueSolutionSchema = Output<typeof lawgueSolutionSchema>

type DocumentFormType = {
  bg: string
  formlead?: string
  formtitle?: string
  endpoint?: string
  content?: any
  isPreview?: boolean
  departments: Department[]
  opportunities: Opportunity[]
}

export const DocumentForm: React.FC<DocumentFormType> = memo(
  ({ formtitle = '資料請求', bg, formlead, departments, opportunities }) => {
    const router = useRouter()
    const pathname = usePathname()
    const [ImgDocState, setImgDocState] = useState<string>('')
    const { sendApi, isSendError } = useSendApi(
      '/lawgue-solution',
      '/thanks_download/'
    )
    const [isButtonClicked, setIsButtonClicked] = useState(false)

    const [firstViewRoute, setFirstViewRoute] = useState('')
    const [lastViewRoute, setLastViewRoute] = useState('')
    const [salesforceIdValue, setSalesforceIdValue] = useState('')
    const { pushThanks } = useThanksForAppRouter()
    const [localError, setLocalError] = useState(false)

    useEffect(() => {
      const fullPath = `${window.location.pathname}${window.location.search}`

      if (!sessionStorage.getItem('firstViewURL')) {
        sessionStorage.setItem('firstViewURL', fullPath)
      }

      setFirstViewRoute(sessionStorage.getItem('firstViewURL') || '/')
      setLastViewRoute(fullPath)
    }, [])

    const handleOnSubmit: SubmitHandler<LawgueSolutionSchema> = useCallback(
      async (data) => {
        const params = new URLSearchParams(window.location.search)
        const formData = {
          ...data,
          salesforceId: salesforceIdValue,
          first_view_route: firstViewRoute,
          last_view_route: lastViewRoute,
          utm_params: Object.fromEntries(params)
        }
        // console.log('Submitting form data:', formData)
        setIsButtonClicked(true)
        setLocalError(false)
        await sendApi(JSON.stringify(formData))
          .then(() => {
            pushThanks('thanks_download')
          })
          .catch((error) => {
            console.error('Sending API request failed:', error)
            setLocalError(true)
          })
      },
      [sendApi, firstViewRoute, lastViewRoute, salesforceIdValue, pushThanks]
    )

    const {
      register,
      handleSubmit,
      formState: { errors: formatError, isLoading }
    } = useForm<Output<typeof lawgueSolutionSchema>>({
      resolver: valibotResolver(lawgueSolutionSchema)
    })

    useEffect(() => {
      if (pathname) {
        if (pathname.includes('flow')) {
          setSalesforceIdValue('/l/939003/2022-02-27/btm1')
          setImgDocState('img_doc')
        } else if (pathname.includes('functions')) {
          setSalesforceIdValue('/l/939003/2022-02-27/bthy')
          setImgDocState('img_doc')
        } else if (pathname.includes('contract')) {
          setSalesforceIdValue('/l/939003/2022-02-27/btjf')
          setImgDocState('img_doc_contract')
        } else if (pathname.includes('law')) {
          setSalesforceIdValue('/l/939003/2022-05-22/27qwv')
          setImgDocState('img_doc_law')
        } else if (pathname.includes('regulation')) {
          setSalesforceIdValue('/l/939003/2022-02-27/btkf')
          setImgDocState('img_doc_regulation')
        } else if (pathname.includes('translation')) {
          setSalesforceIdValue('/l/939003/2022-02-27/btl3')
          setImgDocState('img_doc_translation')
        } else if (pathname.includes('voice')) {
          setSalesforceIdValue('/l/939003/2022-02-27/btmh')
          setImgDocState('img_doc_voice')
        } else {
          setSalesforceIdValue('')
          setImgDocState('img_doc')
        }
      }
    }, [pathname])

    return (
      <Section bg={`${bg}`}>
        <div
          className="mx-auto w-full px-6 py-10 bg-white md:w-[90%] lg:w-[960px] lg:py-20 lg:pl-24 lg:pr-20"
          id="request"
          tabIndex={-1}
        >
          <Heading
            Tag="h2"
            size="text-lg lg:text-4xl"
            textColor="textblack"
            textalign="text-center"
          >
            {`${formtitle}`}
          </Heading>

          <Lead
            textalign="text-center"
            size="text-sm"
            lead={`LAWGUEにご興味をお持ちいただき、誠にありがとうございます。<br>ご登録いただいたメールアドレスへ${
              formlead ? formlead : 'LAWGUEの資料'
            }のダウンロードURLをお送りいたします。`}
            Tag="p"
            margin="mt-6"
          />
          <form
            className="mt-6 lg:mt-14"
            onSubmit={handleSubmit(handleOnSubmit)}
          >
            <fieldset>
              <div className="flex flex-col lg:flex-row items-start justify-between">
                <div className="hidden lg:block">
                  <img src={`/images/${ImgDocState}.png`} alt="" />
                </div>
                <div className="w-full lg:w-[370px]">
                  <fieldset>
                    <div className="w-full mx-auto">
                      <input
                        type="hidden"
                        name="first_view_route"
                        defaultValue="/"
                      />
                      <input
                        type="hidden"
                        name="last_view_route"
                        defaultValue="/contact"
                      />
                      <input
                        type="hidden"
                        value={salesforceIdValue}
                        {...register('salesforceId')}
                      />
                      <div className="relative mb-6 done_input">
                        <FieldBlock
                          labelText={'貴社名／貴所名'}
                          htmlFor={'company'}
                          required
                          error={formatError.company}
                        >
                          <Input
                            id={'company'}
                            type="text"
                            placeholder="ローグ株式会社"
                            autoComplete="company"
                            register={register('company')}
                          />
                        </FieldBlock>
                      </div>
                      <div className="relative mb-6 done_input">
                        <div className="flex flex-col lg:flex-row">
                          <div className="w-full lg:w-1/2 mb-6 lg:mb-0 lg:mr-6 relative done_input">
                            <FieldBlock
                              labelText={'姓'}
                              htmlFor={'lastname'}
                              required
                              error={formatError.lastname}
                            >
                              <Input
                                id={'lastname'}
                                type="text"
                                placeholder="山田"
                                autoComplete="family-name"
                                register={register('lastname')}
                              />
                            </FieldBlock>
                          </div>
                          <div className="w-full lg:w-1/2 relative done_input">
                            <FieldBlock
                              labelText={'名'}
                              htmlFor={'firstname'}
                              required
                              error={formatError.firstname}
                            >
                              <Input
                                id={'firstname'}
                                type="text"
                                placeholder="太郎"
                                autoComplete="given-name"
                                register={register('firstname')}
                              />
                            </FieldBlock>
                          </div>
                        </div>
                      </div>
                      <div className="relative my-6 done_input">
                        <FieldBlock
                          labelText={'メールアドレス'}
                          htmlFor={'email'}
                          required
                          error={formatError.email}
                        >
                          <Input
                            id={'email'}
                            type="email"
                            placeholder="lawgue@mail.com"
                            autoComplete="email"
                            register={register('email')}
                          />
                        </FieldBlock>
                      </div>
                      <div className="relative my-6 done_input">
                        <FieldBlock
                          labelText={'電話番号（ハイフンあり）'}
                          htmlFor={'phone_number'}
                          required
                          error={formatError.phone_number}
                        >
                          <Input
                            id={'phone_number'}
                            type="text"
                            placeholder="080-0000-0000"
                            autoComplete="phone_number"
                            register={register('phone_number')}
                          />
                        </FieldBlock>
                      </div>
                      <div className="relative my-6 done_input">
                        <FieldBlock
                          labelText={'部署区分'}
                          htmlFor={'select_form1'}
                          required
                          error={formatError.select_form1}
                        >
                          <Select
                            id={'select_form1'}
                            register={register('select_form1')}
                            options={[
                              { label: '選択してください。', value: '' },
                              ...departments.map((dept) => ({
                                label: dept.title,
                                value: dept.title
                              }))
                            ]}
                          />
                        </FieldBlock>
                      </div>
                      <div className="relative my-6 done_input">
                        <FieldBlock labelText={'部署名'} htmlFor={'department'}>
                          <Input
                            id={'department'}
                            type="text"
                            placeholder="法務部"
                            autoComplete="department"
                            register={register('department')}
                          />
                        </FieldBlock>
                      </div>
                      <div className="relative my-6 done_input">
                        <FieldBlock
                          labelText={'従業員数'}
                          htmlFor={'select_form2'}
                          required
                          error={formatError.select_form2}
                        >
                          <Select
                            id={'select_form2'}
                            register={register('select_form2')}
                            options={[
                              {
                                label: '選択してください。',
                                value: ''
                              },
                              {
                                label: '1～10人',
                                value: '1～10人'
                              },
                              {
                                label: '11～50人',
                                value: '11～50人'
                              },
                              {
                                label: '51～100人',
                                value: '51～100人'
                              },
                              {
                                label: '101～300人',
                                value: '101～300人'
                              },
                              {
                                label: '301～500人',
                                value: '301～500人'
                              },
                              {
                                label: '501～1,000人',
                                value: '501～1,000人'
                              },
                              {
                                label: '1,001～3,000人',
                                value: '1,001～3,000人'
                              },
                              {
                                label: '3,001～5,000人',
                                value: '3,001～5,000人'
                              },
                              {
                                label: '5,001～10,000人',
                                value: '5,001～10,000人'
                              },
                              {
                                label: '10,001人以上',
                                value: '10,001人以上'
                              }
                            ]}
                          />
                        </FieldBlock>
                      </div>
                      <div className="relative mt-6 done_input">
                        <FieldBlock
                          labelText={'LAWGUEを知ったきっかけ'}
                          htmlFor={'select_form3'}
                          required
                          error={formatError.select_form3}
                        >
                          <Select
                            id={'select_form3'}
                            register={register('select_form3')}
                            options={opportunities.map((opt) => ({
                              label: opt.label,
                              value: opt.value
                            }))}
                          />
                        </FieldBlock>
                      </div>
                    </div>
                    <div className="relative mt-6">
                      <FieldBlock
                        labelText={'具体的なお悩みがあればご記入ください'}
                        htmlFor={'textarea'}
                      >
                        <Textarea
                          id={'textarea'}
                          placeholder="例）文書作成業務の効率化に困っている"
                          autoComplete="textarea"
                          register={register('textarea')}
                        />
                      </FieldBlock>
                    </div>
                    <div className="relative my-6">
                      <Checkbox
                        htmlfor="checkbox"
                        required
                        require={true}
                        register={register}
                      />
                      <ValidationError error={formatError.checkbox} />
                    </div>
                    <Lead
                      textalign="text-left"
                      size="text-xs"
                      lead="同業他社および当社の競合企業にあたる企業の方の資料請求はご遠慮させていただきます。何卒ご了承ください。"
                      Tag="p"
                      margin="mt-6"
                    />
                  </fieldset>
                  <div className="text-center mt-6 lg:mt-14">
                    <Button
                      size="medium"
                      bg="visible"
                      isLoading={isButtonClicked || isLoading}
                      label={isButtonClicked ? '送信中' : '送信する'}
                      type="submit"
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
          {(isSendError || localError) && (
            <div className="py-4 px-6 mt-10 bg-[#FF413614] border-[#FF4136] border-solid border-[1px] rounded-[4px] text-left">
              <p className="text-base text-[#FF4136]">
                エラーが起こったため送信することができませんでした
              </p>
              <p className="text-base mt-6">
                お手数ですがもう一度ご送信ください。
                <br />
                それでも送信できない場合は、すこし時間を置いてお試しくださいませ。
              </p>
            </div>
          )}
        </div>
      </Section>
    )
  }
)

DocumentForm.displayName = 'DocumentForm'
